/** Valores por defecto para respuestas de confronta (evita crash por campos undefined). */

const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const emptyCobradosSide = () => ({
  subtotal: 0,
  iva: 0,
  total: 0,
  count: 0,
  pue: { subtotal: 0, iva: 0, total: 0, count: 0 },
  pago: { subtotal: 0, iva: 0, total: 0, count: 0 },
});

const emptyNominaSide = () => ({
  percepciones: 0,
  isr: 0,
  neto: 0,
  count: 0,
});

const emptyResicoSide = () => ({
  ingresos_subtotal: 0,
  ingresos_iva: 0,
  egresos_subtotal: 0,
  egresos_iva: 0,
  nomina: 0,
  base_isr: 0,
  isr: 0,
  iva_cargo: 0,
});

const emptyCobradosSummary = () => ({
  matched: 0,
  mismatched: 0,
  solo_en_excel: 0,
  solo_en_sistema: 0,
  fuera_de_periodo: 0,
});

const emptyNominaSummary = () => ({
  ...emptyCobradosSummary(),
  total_workers: 0,
});

const mergeSide = (raw, factory, keys) => {
  const base = factory();
  const src = raw && typeof raw === "object" ? raw : {};
  const out = { ...base };
  keys.forEach((key) => {
    if (key in src) out[key] = n(src[key]);
  });
  return out;
};

const emptyConsideracionesSat = () => ({
  ventana_dias_habiles: 5,
  count: 0,
  items: [],
  nota: "",
});

const mergeConsideracionesSat = (raw) => {
  const src = raw && typeof raw === "object" ? raw : {};
  return {
    ...emptyConsideracionesSat(),
    ventana_dias_habiles: n(src.ventana_dias_habiles) || 5,
    count: n(src.count),
    items: Array.isArray(src.items) ? src.items : [],
    nota: typeof src.nota === "string" ? src.nota : "",
  };
};
const mergeCobradosSide = (raw) => {
  const src = raw && typeof raw === "object" ? raw : {};
  return {
    ...mergeSide(src, emptyCobradosSide, ["subtotal", "iva", "total", "count"]),
    pue: {
      subtotal: n(src.pue?.subtotal),
      iva: n(src.pue?.iva),
      total: n(src.pue?.total),
      count: n(src.pue?.count),
    },
    pago: {
      subtotal: n(src.pago?.subtotal),
      iva: n(src.pago?.iva),
      total: n(src.pago?.total),
      count: n(src.pago?.count),
    },
  };
};

const mergeNominaSide = (raw) => mergeSide(raw, emptyNominaSide, ["percepciones", "isr", "neto", "count"]);

const mergeResicoSide = (raw) =>
  mergeSide(raw, emptyResicoSide, [
    "ingresos_subtotal",
    "ingresos_iva",
    "egresos_subtotal",
    "egresos_iva",
    "nomina",
    "base_isr",
    "isr",
    "iva_cargo",
  ]);

/**
 * Normaliza payload de confronta antes de renderizar.
 *
 * @param {object|null|undefined} data
 * @param {"cobrados"|"nominas"|"resico"|"resicoDetalle"} [variant="cobrados"]
 */
export function normalizeConfrontaResponse(data, variant = "cobrados") {
  const src = data && typeof data === "object" ? data : {};

  if (variant === "resico") {
    return {
      ...src,
      year: src.year ?? null,
      regimen: src.regimen ?? "",
      totales: {
        sistema: mergeResicoSide(src.totales?.sistema),
        excel: mergeResicoSide(src.totales?.excel),
        diff: mergeResicoSide(src.totales?.diff),
      },
      meses: Array.isArray(src.meses) ? src.meses : [],
    };
  }

  if (variant === "resicoDetalle") {
    return {
      ...src,
      ingresos: Array.isArray(src.ingresos) ? src.ingresos : [],
      egresos: Array.isArray(src.egresos) ? src.egresos : [],
      nominas: Array.isArray(src.nominas) ? src.nominas : [],
      excel_ingresos: Array.isArray(src.excel_ingresos) ? src.excel_ingresos : [],
      excel_egresos: Array.isArray(src.excel_egresos) ? src.excel_egresos : [],
      excel_nominas: Array.isArray(src.excel_nominas) ? src.excel_nominas : [],
    };
  }

  if (variant === "nominas") {
    const summarySrc = src.summary && typeof src.summary === "object" ? src.summary : {};
    const summary = emptyNominaSummary();
    Object.keys(summary).forEach((key) => {
      summary[key] = n(summarySrc[key]);
    });
    return {
      ...src,
      totales: {
        sistema: mergeNominaSide(src.totales?.sistema),
        excel: mergeNominaSide(src.totales?.excel),
        diff: mergeNominaSide(src.totales?.diff),
      },
      summary,
      consideraciones_sat: mergeConsideracionesSat(src.consideraciones_sat),
      matched: Array.isArray(src.matched) ? src.matched : [],
      mismatched: Array.isArray(src.mismatched) ? src.mismatched : [],
      solo_en_excel: Array.isArray(src.solo_en_excel) ? src.solo_en_excel : [],
      solo_en_sistema: Array.isArray(src.solo_en_sistema) ? src.solo_en_sistema : [],
      by_worker: src.by_worker && typeof src.by_worker === "object" ? src.by_worker : {},
    };
  }

  // cobrados (ingresos / egresos)
  const summarySrc = src.summary && typeof src.summary === "object" ? src.summary : {};
  const summary = emptyCobradosSummary();
  Object.keys(summary).forEach((key) => {
    summary[key] = n(summarySrc[key]);
  });

  const apiSistemaDisplay =
    src.totales?.sistema_display && typeof src.totales.sistema_display === "object"
      ? src.totales.sistema_display
      : null;

  return {
    ...src,
    totales: {
      sistema: mergeCobradosSide(src.totales?.sistema),
      sistema_display: apiSistemaDisplay
        ? {
            ...mergeCobradosSide(apiSistemaDisplay),
            pago_excluido_resumen: apiSistemaDisplay.pago_excluido_resumen || null,
          }
        : null,
      complementos_p_trazabilidad: Array.isArray(src.totales?.complementos_p_trazabilidad)
        ? src.totales.complementos_p_trazabilidad
        : [],
      complemento_p_iva_pendiente: n(src.totales?.complemento_p_iva_pendiente),
      excel: mergeCobradosSide(src.totales?.excel),
      excel_contabilidad: mergeCobradosSide(src.totales?.excel_contabilidad),
      excel_display: mergeCobradosSide(src.totales?.excel_display),
      diff: mergeCobradosSide(src.totales?.diff),
      diff_contabilidad_vs_sistema: mergeCobradosSide(src.totales?.diff_contabilidad_vs_sistema),
    },
    summary,
    consideraciones_sat: mergeConsideracionesSat(src.consideraciones_sat),
    matched: Array.isArray(src.matched) ? src.matched : [],
    mismatched: Array.isArray(src.mismatched) ? src.mismatched : [],
    solo_en_excel: Array.isArray(src.solo_en_excel) ? src.solo_en_excel : [],
    solo_en_sistema: Array.isArray(src.solo_en_sistema) ? src.solo_en_sistema : [],
    fuera_de_periodo: Array.isArray(src.fuera_de_periodo) ? src.fuera_de_periodo : [],
  };
}
